import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useAcceptEula, useAuthentication, useGetEula } from '@connect-x-app/hybrid-state';
import { Text } from '@connect-x-app/hybrid-ui';
import { QueryKeys } from '@connect-x-app/shared-domain-models';
import { EventType } from '@connect-x-app/shared-types';
import { AviobookLogo, COLORS } from '@connect-x-app/shared-ui';
import { useFirebaseConfig, useLogout } from '@connect-x-app/web-state';
import { LICENSE_TEST_IDS } from '@connect-x-app/web-test-ids';
import { Button, Icon } from '@connect-x-app/web-ui';
import { useQueryClient } from '@tanstack/react-query';

import './licenseAgreement.scss';

export const LicenseAgreement = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { setUser, user } = useAuthentication();
  const { logEvent } = useFirebaseConfig();
  const logout = useLogout();
  const queryClient = useQueryClient();
  const { data } = useGetEula();

  const { mutate: acceptAgreement } = useAcceptEula({
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [QueryKeys.AUTHENTICATE] });
      if (user) {
        setUser({ ...user, hasAcceptedLicenseAgreement: true });
      }
      navigate('/dashboard');
    },
  });

  const [checked, setChecked] = useState(false);

  const onClickCheckbox = () => {
    setChecked(!checked);
  };

  const onContinue = () => {
    if (data?.version) {
      acceptAgreement(data.version);
    }
  };

  const onPressBack = () => {
    logEvent(EventType.LOGOUT, { reason: 'Manual logout' });
    logout();
  };

  return (
    <main className="license-agreement-container" data-test={LICENSE_TEST_IDS.agreement.page}>
      <header>
        <button className="license-agreement-button" onClick={onPressBack}>
          <Icon colorName={COLORS.zulu.$08} name="arrowLeft" />
          <Text>{t('SHARED.BUTTONS.BACK')}</Text>
        </button>
      </header>
      <section className="license-agreement-content-container">
        <div className="license-agreement-content-container-inner">
          <img alt="logo" src={AviobookLogo} width={160} />
          <Text as="h1" className="title" size="XL" weight="bold">
            {t('LICENSE_AGREEMENT.WEB.TITLE').toUpperCase()}
          </Text>
          <Text as="p" className="subtitle" size="SM" weight="bold">
            {t('LICENSE_AGREEMENT.WEB.SUBTITLE')}
          </Text>
          <Text size="XS" wrap>
            {data?.eulaText}
          </Text>
        </div>
      </section>
      <footer className="license-agreement-checkbox-container">
        <div className="license-agreement-checkbox-inner">
          <div className="checkbox-container">
            <input
              checked={checked}
              data-test={LICENSE_TEST_IDS.agreement.acceptCheckbox}
              id="license-agreement-checkbox"
              onChange={onClickCheckbox}
              type="checkbox"
            />
            <Text as="span" className="checkbox-label" color={COLORS.zulu.$08} size="SM">
              <label htmlFor="license-agreement-checkbox">{t('LICENSE_AGREEMENT.WEB.CHECKBOX_LABEL')}</label>
            </Text>
          </div>
          <Button disabled={!checked} onClick={onContinue} testId={LICENSE_TEST_IDS.agreement.accept}>
            {t('LICENSE_AGREEMENT.CONTINUE')}
          </Button>
        </div>
      </footer>
    </main>
  );
};
